'use client';

/**
 * useActivePromo — client hook exposing the current sitewide promo campaign
 * (same source as the PromoBanner: /api/v2/commerce/active-banner).
 *
 * Used by PLP cards and the configurator to render "was / now with code"
 * pricing. Returns null while loading or when no percentage promo is active,
 * so callers can simply hide the promo treatment.
 */

import { useEffect, useState } from 'react';

export interface ActivePromo {
  code: string;
  discountPercent: number; // e.g. 10
}

let cached: ActivePromo | null | undefined; // undefined = not fetched yet

export function promoPrice(listPrice: number, promo: ActivePromo): number {
  return Math.round(listPrice * (1 - promo.discountPercent / 100) * 100) / 100;
}

export function useActivePromo(): ActivePromo | null {
  const [promo, setPromo] = useState<ActivePromo | null>(cached ?? null);

  useEffect(() => {
    if (cached !== undefined) { setPromo(cached); return; }
    let alive = true;
    fetch('/api/v2/commerce/active-banner')
      .then((r) => r.json())
      .then((d) => {
        const b = d?.banner;
        cached = b?.code && b?.discountPercent ? { code: b.code, discountPercent: b.discountPercent } : null;
        if (alive) setPromo(cached);
      })
      .catch(() => { cached = null; if (alive) setPromo(null); });
    return () => { alive = false; };
  }, []);

  return promo;
}
